import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../../../lib/http/ApiError';
import { useFetch } from '../../../hooks/useFetch';
import type { Contract } from '../contractTypes';
import { ContractDetailApiFactory } from '../api/contractDetailApi';

type CacheKey = string | 'current';

interface UseContractDetailResult {
  contract: Contract | null;
  isLoading: boolean;
  error: ApiError | Error | null;
  retry: () => void;
}

/**
 * Fetches and caches the contract detail for a given employee.
 *
 * - contractId === null  →  GET /employees/:id/contracts/current
 * - contractId === string →  GET /employees/:id/contracts/:contractId
 *
 * Results are memoised by contractId for the lifetime of the component,
 * so navigating back to a previously-viewed contract is instant.
 * The cache is cleared when lang changes to ensure fresh localised labels.
 * Call retry() to bypass the cache and re-fetch (e.g. after an error).
 */
export function useContractDetail(
  employeeId: string,
  contractId: string | null,
  lang: string,
): UseContractDetailResult {
  const [client] = useFetch();
  const api = useMemo(() => ContractDetailApiFactory(client, lang), [client, lang]);

  const cache = useRef(new Map<CacheKey, Contract>());

  const [contract, setContract] = useState<Contract | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  const retry = useCallback(() => {
    cache.current.delete(contractId ?? 'current');
    setRetryKey(k => k + 1);
  }, [contractId]);

  // Declared before the fetch effect so it runs first in the same commit,
  // ensuring stale localised labels are evicted before the fetch effect reads the cache.
  useEffect(() => {
    cache.current.clear();
  }, [lang]);

  useEffect(() => {
    if (!employeeId) return;

    const cacheKey: CacheKey = contractId ?? 'current';
    const cached = cache.current.get(cacheKey);

    if (cached) {
      setContract(cached);
      setIsLoading(false);
      setError(null);
      return;
    }

    const ctrl = new AbortController();
    setContract(null);
    setError(null);
    setIsLoading(true);

    const fetchPromise =
      contractId != null
        ? api.getById(employeeId, contractId, { signal: ctrl.signal })
        : api.getCurrentByEmployeeId(employeeId, { signal: ctrl.signal });

    fetchPromise
      .then(c => {
        if (c) cache.current.set(cacheKey, c);
        setContract(c);
      })
      .catch(err => {
        if (err instanceof Error && err.name === 'AbortError') return;
        if (ApiError.isApiError(err) && err.isNotFound && contractId === null) return; // no active contract → keep contract=null, no error
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => setIsLoading(false));

    return () => ctrl.abort();
  }, [api, employeeId, contractId, retryKey]);

  return { contract, isLoading, error, retry };
}
